import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

type Section = Database['public']['Tables']['sections']['Row'];
type SectionInsert = Database['public']['Tables']['sections']['Insert'];
type SectionUpdate = Database['public']['Tables']['sections']['Update'];
type Channel = Database['public']['Tables']['channels']['Row'];

export type SectionWithChannels = Section & {
  channels: Channel[];
};

export function useSections() {
  return useQuery({
    queryKey: ['sections'],
    queryFn: async (): Promise<Section[]> => {
      const { data, error } = await supabase
        .from('sections')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');

      if (error) throw error;
      return data || [];
    },
    staleTime: 1000 * 60 * 5, // 5 minutes - sections rarely change
  });
}

export function useAllSections() {
  return useQuery({
    queryKey: ['sections', 'all'],
    queryFn: async (): Promise<Section[]> => {
      const { data, error } = await supabase
        .from('sections')
        .select('*')
        .order('sort_order');

      if (error) throw error;
      return data || [];
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useSectionsWithChannels() {
  return useQuery({
    queryKey: ['sections', 'with-channels'],
    queryFn: async (): Promise<SectionWithChannels[]> => {
      // Fetch sections
      const { data: sections, error: sectionsError } = await supabase
        .from('sections')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');

      if (sectionsError) throw sectionsError;

      // Fetch all active channels
      const { data: channels, error: channelsError } = await supabase
        .from('channels')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');

      if (channelsError) throw channelsError;

      // Group channels by section
      const sectionsWithChannels: SectionWithChannels[] = (sections || []).map((section) => ({
        ...section,
        channels: (channels || []).filter((channel) => channel.section_id === section.id),
      }));

      // Add an "Uncategorized" section for channels without a section
      const uncategorizedChannels = (channels || []).filter((channel) => !channel.section_id);
      if (uncategorizedChannels.length > 0) {
        sectionsWithChannels.push({
          id: 'uncategorized',
          name: 'Other',
          emoji: '📌',
          sort_order: 999,
          is_collapsed: false,
          is_active: true,
          created_at: new Date().toISOString(),
          channels: uncategorizedChannels,
        });
      }

      return sectionsWithChannels;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useCreateSection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (section: SectionInsert) => {
      const { data, error } = await supabase
        .from('sections')
        .insert(section)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sections'] });
    },
  });
}

export function useUpdateSection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: SectionUpdate & { id: string }) => {
      const { data, error } = await supabase
        .from('sections')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sections'] });
    },
  });
}

export function useDeleteSection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('sections')
        .update({ is_active: false })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sections'] });
    },
  });
}
